import React, { useState, useEffect } from 'react';
import { X, CreditCard, Building2, Smartphone, Bitcoin, DollarSign, AlertTriangle } from 'lucide-react';
import { useAccount } from '../contexts/AccountContext';
import { useTheme } from '../contexts/ThemeContext';

interface WithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  preFilledAmount?: string;
}

const WithdrawalModal: React.FC<WithdrawalModalProps> = ({ isOpen, onClose, preFilledAmount }) => {
  const { withdraw, accountBalance, isLoading } = useAccount();
  const { theme } = useTheme();
  const [amount, setAmount] = useState<string>(preFilledAmount || '');
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [step, setStep] = useState<'amount' | 'method' | 'confirmation'>('amount');

  // Update amount when preFilledAmount changes
  useEffect(() => {
    if (preFilledAmount) {
      setAmount(preFilledAmount);
      setStep('amount');
    }
  }, [preFilledAmount]);

  const paymentMethods = [
    {
      id: 'bank',
      name: 'Bank Transfer',
      icon: Building2,
      description: 'Direct bank transfer',
      color: 'bg-blue-600',
      processingTime: '1-3 business days',
      fee: 15,
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: CreditCard,
      description: 'PayPal account',
      color: 'bg-blue-500',
      processingTime: '1-2 business days',
      fee: 10,
    },
    {
      id: 'crypto',
      name: 'Cryptocurrency',
      icon: Bitcoin,
      description: 'Bitcoin, Ethereum, etc.',
      color: 'bg-yellow-600',
      processingTime: '1-24 hours',
      fee: 5,
    },
  ];

  const handleMethodSelect = (methodId: string) => {
    setSelectedMethod(methodId);
  };

  const handleNext = () => {
    if (step === 'amount') {
      const numAmount = parseFloat(amount);
      if (numAmount && numAmount >= 50 && numAmount <= accountBalance.liveBalance) {
        setStep('method');
      }
    } else if (step === 'method') {
      if (selectedMethod) {
        setStep('confirmation');
      }
    }
  };

  const handleBack = () => {
    if (step === 'method') {
      setStep('amount');
    } else if (step === 'confirmation') {
      setStep('method');
    }
  };

  const handleConfirmWithdrawal = async () => {
    try {
      const numAmount = parseFloat(amount);
      const method = paymentMethods.find(m => m.id === selectedMethod)?.name || selectedMethod;
      
      if (numAmount && method) {
        await withdraw(numAmount, method);
        onClose();
        // Reset form
        setAmount('');
        setSelectedMethod('');
        setStep('amount');
      }
    } catch (error) {
      console.error('Withdrawal failed:', error);
    }
  };

  const getSelectedMethod = () => {
    return paymentMethods.find(m => m.id === selectedMethod);
  };

  const getTotalAmount = () => {
    const numAmount = parseFloat(amount) || 0;
    const method = getSelectedMethod();
    return numAmount + (method?.fee || 0);
  };

  const canProceed = () => {
    if (step === 'amount') {
      const numAmount = parseFloat(amount);
      return numAmount && numAmount >= 50 && numAmount <= accountBalance.liveBalance;
    } else if (step === 'method') {
      return selectedMethod !== '';
    }
    return true;
  };

  const getAmountError = () => {
    const numAmount = parseFloat(amount);
    if (!numAmount) return '';
    if (numAmount < 50) return 'Minimum withdrawal amount is $50';
    if (numAmount > accountBalance.liveBalance) return 'Insufficient balance';
    return '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Modal */}
      <div className={`relative rounded-lg shadow-xl w-full max-w-md mx-4 ${
        theme === 'light' ? 'bg-white' : 'bg-gray-800'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          theme === 'light' ? 'border-gray-200' : 'border-gray-700'
        }`}>
          <h2 className={`text-xl font-semibold ${
            theme === 'light' ? 'text-gray-900' : 'text-gray-100'
          }`}>Withdraw Funds</h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              theme === 'light' ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'amount' ? 'bg-[#2AB7A6] text-black' : theme === 'light' ? 'bg-gray-300 text-gray-700' : 'bg-gray-600 text-gray-300'
              }`}>
                1
              </div>
              <div className={`w-8 h-1 ${
                theme === 'light' ? 'bg-gray-300' : 'bg-gray-600'
              }`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'method' ? 'bg-[#2AB7A6] text-black' : step === 'confirmation' ? 'bg-[#2AB7A6] text-black' : theme === 'light' ? 'bg-gray-300 text-gray-700' : 'bg-gray-600 text-gray-300'
              }`}>
                2
              </div>
              <div className={`w-8 h-1 ${
                theme === 'light' ? 'bg-gray-300' : 'bg-gray-600'
              }`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'confirmation' ? 'bg-[#2AB7A6] text-black' : theme === 'light' ? 'bg-gray-300 text-gray-700' : 'bg-gray-600 text-gray-300'
              }`}>
                3
              </div>
            </div>
          </div>

                     {/* Step 1: Amount Selection */}
           {step === 'amount' && (
             <div>
                               <h3 className={`text-xs font-medium mb-4 ${
                                 theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                               }`}>Withdrawal Amount</h3>
               
               {/* Available Balance */}
               <div className={`rounded-lg p-4 mb-6 ${
                 theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'bg-gray-700'
               }`}>
                 <div className="flex items-center justify-between mb-2">
                   <span className={`text-xs ${
                     theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                   }`}>Available Balance</span>
                   <span className={`text-xs font-medium ${
                     theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                   }`}>${accountBalance.liveBalance.toLocaleString()}</span>
                 </div>
                 <div className="flex items-center justify-between">
                   <span className={`text-xs ${
                     theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                   }`}>Free Margin</span>
                   <span className={`text-xs font-medium ${
                     theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                   }`}>${accountBalance.freeMargin.toLocaleString()}</span>
                 </div>
               </div>

               {/* Amount Input */}
               <div className="mb-6">
                 <label className={`block text-xs font-medium mb-2 ${
                   theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                 }`}>
                   Withdrawal Amount
                 </label>
                 <div className="relative">
                   <DollarSign className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
                     theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                   }`} />
                   <input
                     type="number"
                     value={amount}
                     onChange={(e) => setAmount(e.target.value)}
                     placeholder="0.00"
                     min="50"
                     max={accountBalance.liveBalance}
                     step="0.01"
                     className={`w-full pl-10 pr-4 py-3 border rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#2AB7A6] focus:border-transparent ${
                       theme === 'light' 
                         ? 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400' 
                         : 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500'
                     }`}
                   />
                 </div>
                 
                 {/* Amount Limits */}
                 <div className="flex space-x-4 mt-2 text-xs">
                   <span className="text-[#2AB7A6]">Min: $50</span>
                   <span className="text-[#2AB7A6]">Max: ${accountBalance.liveBalance.toLocaleString()}</span>
                 </div>

                 {/* Error Message */}
                 {getAmountError() && (
                   <div className="flex items-center space-x-2 mt-2 text-red-400 text-xs">
                     <AlertTriangle className="h-4 w-4" />
                     <span>{getAmountError()}</span>
                   </div>
                 )}
               </div>
             </div>
           )}

                     {/* Step 2: Payment Method Selection */}
           {step === 'method' && (
             <div>
                               <h3 className={`text-xs font-medium mb-4 ${
                                 theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                               }`}>Select Withdrawal Method</h3>
               
               <div className="space-y-3">
                 {paymentMethods.map((method) => {
                   const Icon = method.icon;
                   return (
                     <button
                       key={method.id}
                       onClick={() => handleMethodSelect(method.id)}
                       className={`w-full p-4 rounded-lg transition-all duration-200 text-left ${
                         selectedMethod === method.id
                           ? 'border-[#2AB7A6] bg-[#2AB7A6] text-black'
                           : theme === 'light' 
                             ? 'border !border-gray-300 hover:!border-[#2AB7A6] hover:bg-gray-50' 
                             : 'border-2 border-gray-600 hover:border-gray-500'
                       }`}
                     >
                       <div className="flex items-center space-x-3">
                         <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${method.color}`}>
                           <Icon className="h-5 w-5 text-white" />
                         </div>
                         <div className="flex-1">
                           <div className={`text-xs font-medium ${
                             theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                           }`}>{method.name}</div>
                           <div className={`text-xs ${
                             theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                           }`}>{method.description}</div>
                           <div className="flex items-center justify-between mt-2 text-xs">
                             <span className={theme === 'light' ? 'text-gray-500' : 'text-gray-500'}>Processing: {method.processingTime}</span>
                             <span className={theme === 'light' ? 'text-gray-500' : 'text-gray-500'}>Fee: ${method.fee}</span>
                           </div>
                         </div>
                       </div>
                     </button>
                   );
                 })}
               </div>
             </div>
           )}

                     {/* Step 3: Confirmation */}
           {step === 'confirmation' && (
             <div>
               <h3 className={`text-lg font-medium mb-4 ${
                 theme === 'light' ? 'text-gray-900' : 'text-gray-100'
               }`}>Confirm Withdrawal</h3>
               
               <div className={`rounded-lg p-4 mb-6 ${
                 theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'bg-gray-700'
               }`}>
                 <div className="space-y-3">
                   <div className="flex justify-between">
                     <span className={`text-xs ${
                       theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                     }`}>Amount:</span>
                     <span className={`text-xs font-medium ${
                       theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                     }`}>${parseFloat(amount).toLocaleString()}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className={`text-xs ${
                       theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                     }`}>Withdrawal Method:</span>
                     <span className={`text-xs font-medium ${
                       theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                     }`}>
                       {getSelectedMethod()?.name}
                     </span>
                   </div>
                   <div className="flex justify-between">
                     <span className={`text-xs ${
                       theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                     }`}>Processing Fee:</span>
                     <span className={`text-xs font-medium ${
                       theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                     }`}>${getSelectedMethod()?.fee}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className={`text-xs ${
                       theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                     }`}>Processing Time:</span>
                     <span className={`text-xs font-medium ${
                       theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                     }`}>{getSelectedMethod()?.processingTime}</span>
                   </div>
                   <div className={`border-t pt-3 ${
                     theme === 'light' ? 'border-gray-200' : 'border-gray-600'
                   }`}>
                     <div className="flex justify-between">
                       <span className={`text-xs font-medium ${
                         theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                       }`}>Total:</span>
                       <span className="text-[#2AB7A6] font-bold text-lg">${getTotalAmount().toLocaleString()}</span>
                     </div>
                   </div>
                 </div>
               </div>

               <div className={`text-xs text-center ${
                 theme === 'light' ? 'text-gray-600' : 'text-gray-400'
               }`}>
                 Click confirm to submit your withdrawal request. You will receive a confirmation email once processed.
               </div>
             </div>
           )}

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-6">
            {step !== 'amount' && (
              <button
                onClick={handleBack}
                className={`flex-1 px-4 py-3 rounded-lg transition-colors ${
                  theme === 'light' 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                }`}
              >
                Back
              </button>
            )}
            
            {step !== 'confirmation' ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 bg-[#2AB7A6] text-white hover:bg-[#25a595] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleConfirmWithdrawal}
                disabled={isLoading}
                className="flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 bg-[#2AB7A6] text-white hover:bg-[#25a595] disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Confirm Withdrawal'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalModal;
